import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Package,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  Wand2,
  ClipboardCheck,
  Lightbulb,
  Inbox,
} from 'lucide-react';
import {
  getInventoryItems,
  createInventoryItem,
  updateInventoryItem,
  deleteInventoryItem,
  getSuppliers,
  updateRestaurantStock,
  getInventorySettings,
} from '../services/api';
import { CANONICAL_UNITS } from '../utils/units';
import {
  KINDS,
  EMPTY_INVENTORY_FORM,
  prepareInventoryItemForEdit,
  buildInventoryPayload,
  buildLocalMinStockChange,
  effectiveMinStock,
  getStockStatus,
} from '../utils/inventoryForm';
import { formatCurrency } from '../utils/formatCurrency';
import HelpIcon from '../components/help/HelpIcon';
import InventoryCoveragePanel from '../components/InventoryCoveragePanel';
import SuggestInventoryItemModal from '../components/SuggestInventoryItemModal';
import useEscapeKey from '../hooks/useEscapeKey';
import { inventoryErrorMessage } from '../utils/inventoryErrors';
import './InventoryPage.css';

const STATUS_BADGE = {
  inactive: 'badge-neutral',
  notTracked: 'badge-neutral',
  outOfStock: 'badge-danger',
  lowStock: 'badge-warning',
  expiringSoon: 'badge-warning',
  expired: 'badge-danger',
  normalStock: 'badge-success',
};

function InventoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(EMPTY_INVENTORY_FORM);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // RES-28: el backend dice si este usuario puede editar el catálogo (rol + membresía +
  // `catalogLocked` de la organización). Ante la duda se muestra, y el 403 explica por qué.
  const [canEditCatalog, setCanEditCatalog] = useState(true);
  // Conteos y stock inicial se mueven con movimientos: los habilita `stockWrite` (RES-40)
  const [canWriteStock, setCanWriteStock] = useState(true);
  // RES-46: con el catálogo bloqueado la sucursal sugiere insumos y la organización los revisa
  const [catalogLocked, setCatalogLocked] = useState(false);
  const [canSuggest, setCanSuggest] = useState(false);
  const [canReviewSuggestions, setCanReviewSuggestions] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  // Confirmación de borrado en un modal propio (antes era un confirm() nativo)
  const [itemToDelete, setItemToDelete] = useState(null);

  useEscapeKey(Boolean(itemToDelete), () => setItemToDelete(null));

  const showFeedback = useCallback((type, message) => {
    setFeedback({ type, message });
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, []);

  const loadItems = useCallback(async (includeInactive = showInactive) => {
    try {
      const response = await getInventoryItems(includeInactive ? { includeInactive: true } : {});
      setItems(response.items || []);
    } catch (error) {
      console.error('Error loading inventory:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getInventorySettings()
      .then((settings) => {
        if (settings?.can) {
          setCanEditCatalog(settings.can.catalogWrite !== false);
          setCanWriteStock(settings.can.stockWrite !== false);
          setCatalogLocked(
            settings.can.catalogLocked === true && settings.can.catalogWrite === false
          );
          setCanSuggest(settings.can.suggestionsWrite === true);
          setCanReviewSuggestions(settings.can.suggestionsReview === true);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadItems(showInactive);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  useEffect(() => {
    // Proveedor por defecto del insumo (RES-22); si el módulo no está disponible el selector queda vacío
    getSuppliers()
      .then((response) => setSuppliers(response.suppliers || []))
      .catch((error) => console.error('Error loading suppliers:', error));
  }, []);

  const categories = useMemo(
    () => [...new Set(items.map((item) => item.category).filter(Boolean))].sort(),
    [items]
  );

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData(prepareInventoryItemForEdit(item));
    } else {
      setEditingItem(null);
      setFormData(EMPTY_INVENTORY_FORM);
    }
    setFormErrors({});
    setModalOpen(true);
  };

  const setField = (field, value) => {
    if (formErrors[field]) setFormErrors((prev) => ({ ...prev, [field]: '' }));
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!formData.name.trim()) errors.name = t('customers.validation.nameRequired');
    if (!editingItem && formData.trackStock && formData.stock === '') {
      errors.stock = t('inventory.stockRequired');
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    try {
      const payload = buildInventoryPayload(formData, { isEdit: Boolean(editingItem) });
      if (editingItem) {
        await updateInventoryItem(editingItem.itemId, payload);
        // Mínimo propio de este local (RES-23): vacío vuelve a heredar el de la organización
        const localMin = buildLocalMinStockChange(formData, editingItem);
        if (localMin) await updateRestaurantStock(editingItem.itemId, localMin);
      } else {
        await createInventoryItem(payload);
      }
      setModalOpen(false);
      loadItems();
      showFeedback('success', t('inventory.saveSuccess'));
    } catch (error) {
      console.error('Error saving item:', error);
      showFeedback('error', inventoryErrorMessage(t, error, 'inventory.saveError'));
    }
  };

  const handleDelete = async () => {
    const item = itemToDelete;
    if (!item) return;
    setItemToDelete(null);
    try {
      const result = await deleteInventoryItem(item.itemId);
      loadItems();
      // Con movimientos el backend no borra: desactiva y conserva el historial (RES-22)
      showFeedback(
        'success',
        result?.deactivated ? t('inventory.deactivatedNotice') : t('inventory.deleteSuccess')
      );
    } catch (error) {
      console.error('Error deleting item:', error);
      showFeedback('error', t('inventory.deleteError'));
    }
  };

  const handleQuickStockUpdate = async (item, change) => {
    try {
      const newStock = Math.max(0, parseFloat(item.stock) + change);
      await updateInventoryItem(item.itemId, { stock: newStock });
      // Optimistic update
      setItems((prev) =>
        prev.map((i) => (i.itemId === item.itemId ? { ...i, stock: newStock } : i))
      );
    } catch (error) {
      console.error('Error updating stock:', error);
      loadItems(); // Revert on error
    }
  };

  // Helper to check expiration status
  const getExpirationStatus = (dateStr) => {
    if (!dateStr) return null;
    const todayUTC = Date.UTC(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate()
    );
    const expUTC = Date.UTC(
      new Date(dateStr).getUTCFullYear(),
      new Date(dateStr).getUTCMonth(),
      new Date(dateStr).getUTCDate()
    );

    const diffTime = expUTC - todayUTC;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'expired';
    if (diffDays <= 3) return 'critical';
    if (diffDays <= 7) return 'warning';
    return 'ok';
  };

  const handleSort = useCallback(
    (column) => {
      if (sortColumn === column) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
      setCurrentPage(1);
    },
    [sortColumn]
  );

  const filteredAndSortedItems = useMemo(() => {
    let result = items;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((item) =>
        [item.name, item.unit, item.category, item.sku].some(
          (value) => value && String(value).toLowerCase().includes(query)
        )
      );
    }

    if (kindFilter !== 'all') {
      result = result.filter((item) => (item.kind || 'INGREDIENT') === kindFilter);
    }

    if (statusFilter === 'lowStock') {
      result = result.filter((item) =>
        ['lowStock', 'outOfStock'].includes(getStockStatus(item, null))
      );
    } else if (statusFilter === 'expiring') {
      result = result.filter((item) => {
        const status = getExpirationStatus(item.expirationDate);
        return status === 'expired' || status === 'critical' || status === 'warning';
      });
    }

    result = [...result].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      if (sortColumn === 'refCost') {
        aVal = parseFloat(a.refCost ?? a.cost) || 0;
        bVal = parseFloat(b.refCost ?? b.cost) || 0;
      } else if (sortColumn === 'stock') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortColumn === 'expirationDate') {
        aVal = aVal ? new Date(aVal).getTime() : Infinity;
        bVal = bVal ? new Date(bVal).getTime() : Infinity;
      } else if (sortColumn === 'kind') {
        aVal = t(`inventory.kinds.${a.kind || 'INGREDIENT'}`).toLowerCase();
        bVal = t(`inventory.kinds.${b.kind || 'INGREDIENT'}`).toLowerCase();
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [items, searchQuery, statusFilter, kindFilter, sortColumn, sortDirection, t]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSortedItems.length / itemsPerPage));
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedItems.slice(start, start + itemsPerPage);
  }, [filteredAndSortedItems, currentPage, itemsPerPage]);

  const SortIcon = useCallback(
    ({ column }) => {
      if (sortColumn !== column) return <ChevronsUpDown size={14} />;
      return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
    },
    [sortColumn, sortDirection]
  );

  const kindLabel = (kind) => t(`inventory.kinds.${KINDS.includes(kind) ? kind : 'INGREDIENT'}`);

  if (loading) return <div className="loading">{t('common.loading')}</div>;

  return (
    <div className="inventory-page">
      <div className="page-header">
        <h1>{t('inventory.title')}</h1>
        <div className="inventory-header-actions">
          {canEditCatalog && (
            <button
              type="button"
              className="btn btn-ghost inventory-wizard-btn"
              onClick={() => navigate('/inventory/setup')}
            >
              <Wand2 size={16} aria-hidden="true" />
              {t('inventory.entryPoints.wizard')}
            </button>
          )}
          {canWriteStock && (
            <button
              type="button"
              className="btn btn-ghost inventory-count-btn"
              onClick={() => navigate('/inventory/count')}
            >
              <ClipboardCheck size={16} aria-hidden="true" />
              {t('inventory.entryPoints.count')}
            </button>
          )}
          {canReviewSuggestions && (
            <button
              type="button"
              className="btn btn-ghost inventory-suggestions-inbox-btn"
              onClick={() => navigate('/inventory/suggestions')}
            >
              <Inbox size={16} aria-hidden="true" />
              {t('inventory.suggestions.inbox')}
            </button>
          )}
          {canEditCatalog && (
            <button type="button" className="btn btn-primary" onClick={() => handleOpenModal()}>
              {t('inventory.addItem')}
            </button>
          )}
          {!canEditCatalog && catalogLocked && (
            <>
              <span className="catalog-locked-note catalog-managed-note">
                {t('inventory.suggestions.managedByOrg')}
              </span>
              {canSuggest && (
                <button
                  type="button"
                  className="btn btn-ghost inventory-my-suggestions-btn"
                  onClick={() => navigate('/inventory/suggestions')}
                >
                  <Inbox size={16} aria-hidden="true" />
                  {t('inventory.suggestions.mySuggestions')}
                </button>
              )}
              {canSuggest && (
                <button
                  type="button"
                  className="btn btn-primary inventory-suggest-btn"
                  onClick={() => setSuggestOpen(true)}
                >
                  <Lightbulb size={16} aria-hidden="true" />
                  {t('inventory.suggestions.suggest')}
                </button>
              )}
            </>
          )}
          {!canEditCatalog && !catalogLocked && (
            <span className="catalog-locked-note">{t('inventory.catalogLockedNote')}</span>
          )}
        </div>
      </div>

      {feedback && (
        <div
          className={`alert alert-${feedback.type === 'error' ? 'error' : 'success'}`}
          role="alert"
          aria-live="polite"
        >
          {feedback.message}
        </div>
      )}

      <InventoryCoveragePanel
        canEdit={canEditCatalog}
        catalogItems={items}
        onLinked={({ createdItem }) => {
          // Un insumo espejo nuevo tiene que aparecer en la tabla sin recargar la página
          if (createdItem) loadItems();
        }}
      />

      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder={t('inventory.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="search-input"
          />
        </div>
        <div className="filter-buttons">
          <button
            type="button"
            className={`filter-btn${statusFilter === 'all' ? ' active' : ''}`}
            onClick={() => {
              setStatusFilter('all');
              setCurrentPage(1);
            }}
          >
            {t('common.all')}
          </button>
          <button
            type="button"
            className={`filter-btn${statusFilter === 'lowStock' ? ' active' : ''}`}
            onClick={() => {
              setStatusFilter('lowStock');
              setCurrentPage(1);
            }}
          >
            {t('inventory.lowStock')}
          </button>
          <button
            type="button"
            className={`filter-btn${statusFilter === 'expiring' ? ' active' : ''}`}
            onClick={() => {
              setStatusFilter('expiring');
              setCurrentPage(1);
            }}
          >
            {t('inventory.expiring')}
          </button>
          <select
            className="kind-filter"
            value={kindFilter}
            onChange={(e) => {
              setKindFilter(e.target.value);
              setCurrentPage(1);
            }}
            aria-label={t('inventory.kind')}
          >
            <option value="all">{t('inventory.allKinds')}</option>
            {KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {kindLabel(kind)}
              </option>
            ))}
          </select>
          <label className="checkbox-row show-inactive">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => {
                setShowInactive(e.target.checked);
                setCurrentPage(1);
              }}
            />
            {t('inventory.showInactive')}
          </label>
        </div>
      </div>

      <div className="card">
        {items.length === 0 ? (
          <div className="empty-state inventory-empty-catalog">
            <Package size={48} />
            <h2>{t('inventory.entryPoints.emptyTitle')}</h2>
            <p>{t('inventory.entryPoints.emptyHelp')}</p>
            {canEditCatalog && (
              <button
                type="button"
                className="btn btn-primary inventory-setup-cta"
                onClick={() => navigate('/inventory/setup')}
              >
                <Wand2 size={16} aria-hidden="true" />
                {t('inventory.entryPoints.setup')}
              </button>
            )}
          </div>
        ) : filteredAndSortedItems.length === 0 ? (
          <div className="empty-state">
            <Package size={48} />
            <p>{t('inventory.emptyState')}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="inventory-table">
              <thead>
                <tr>
                  <th className="sortable-th" onClick={() => handleSort('name')}>
                    {t('inventory.name')} <SortIcon column="name" />
                  </th>
                  <th className="sortable-th" onClick={() => handleSort('kind')}>
                    {t('inventory.kind')} <SortIcon column="kind" />
                  </th>
                  <th className="sortable-th" onClick={() => handleSort('category')}>
                    {t('inventory.category')} <SortIcon column="category" />
                  </th>
                  <th className="sortable-th" onClick={() => handleSort('stock')}>
                    {t('inventory.stock')} <SortIcon column="stock" />
                  </th>
                  <th>{t('inventory.unit')}</th>
                  <th>{t('inventory.min')}</th>
                  <th className="sortable-th" onClick={() => handleSort('expirationDate')}>
                    {t('inventory.expiration')} <SortIcon column="expirationDate" />
                  </th>
                  <th className="sortable-th" onClick={() => handleSort('refCost')}>
                    {t('inventory.refCost')} <SortIcon column="refCost" />
                  </th>
                  <th>{t('inventory.status')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map((item) => {
                  const expStatus = getExpirationStatus(item.expirationDate);
                  const status = getStockStatus(item, expStatus);
                  const tracked = item.trackStock !== false;
                  const kind = KINDS.includes(item.kind) ? item.kind : 'INGREDIENT';
                  return (
                    <tr key={item.itemId} className={item.isActive === false ? 'inactive-row' : ''}>
                      <td data-label={t('inventory.name')}>
                        {item.name}
                        {item.sku && <span className="item-sku">{item.sku}</span>}
                      </td>
                      <td data-label={t('inventory.kind')}>
                        <span className={`kind-badge ${kind.toLowerCase()}`}>
                          {kindLabel(kind)}
                        </span>
                      </td>
                      <td data-label={t('inventory.category')}>{item.category || '-'}</td>
                      <td data-label={t('inventory.stock')}>
                        {tracked ? (
                          <div className="stock-control">
                            <button
                              type="button"
                              className="btn-icon small"
                              onClick={() => handleQuickStockUpdate(item, -1)}
                              aria-label="-1"
                            >
                              -
                            </button>
                            <span
                              className={
                                Number(item.stock) <= effectiveMinStock(item) ? 'text-danger' : ''
                              }
                            >
                              {item.stock}
                            </span>
                            <button
                              type="button"
                              className="btn-icon small"
                              onClick={() => handleQuickStockUpdate(item, 1)}
                              aria-label="+1"
                            >
                              +
                            </button>
                          </div>
                        ) : (
                          <span className="text-secondary">—</span>
                        )}
                      </td>
                      <td data-label={t('inventory.unit')}>{item.unit}</td>
                      <td data-label={t('inventory.min')}>
                        {tracked ? (
                          <>
                            {effectiveMinStock(item)}
                            {item.minStockInherited !== false && (
                              <span className="min-inherited"> · {t('inventory.inherited')}</span>
                            )}
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td data-label={t('inventory.expiration')}>
                        {tracked && item.expirationDate ? (
                          <span className={`expiration-badge ${expStatus}`}>
                            {new Date(item.expirationDate).toLocaleDateString(undefined, {
                              timeZone: 'UTC',
                            })}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td data-label={t('inventory.refCost')}>
                        {formatCurrency(Number(item.refCost ?? item.cost) || 0)}/{item.unit}
                      </td>
                      <td data-label={t('inventory.status')}>
                        <span className={`badge ${STATUS_BADGE[status]}`}>
                          {t(`inventory.${status}`)}
                        </span>
                      </td>
                      <td data-label={t('common.actions')}>
                        {canEditCatalog ? (
                          <>
                            <button
                              type="button"
                              className="btn-icon"
                              onClick={() => handleOpenModal(item)}
                              aria-label={t('common.edit')}
                            >
                              ✏️
                            </button>
                            <button
                              type="button"
                              className="btn-icon"
                              onClick={() => setItemToDelete(item)}
                              aria-label={t('common.delete')}
                            >
                              🗑️
                            </button>
                          </>
                        ) : (
                          <span className="text-secondary">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="table-pagination">
              <div className="pagination-info">
                {t('inventory.pagination.showing', {
                  from: Math.min(
                    (currentPage - 1) * itemsPerPage + 1,
                    filteredAndSortedItems.length
                  ),
                  to: Math.min(currentPage * itemsPerPage, filteredAndSortedItems.length),
                  total: filteredAndSortedItems.length,
                  defaultValue: '{{from}}–{{to}} de {{total}}',
                })}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-per-page"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  aria-label={t('inventory.pagination.perPage')}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <button
                  type="button"
                  className="btn-icon pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  aria-label={t('common.back')}
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="pagination-page">
                  {currentPage} / {totalPages}
                </span>
                <button
                  type="button"
                  className="btn-icon pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  aria-label={t('inventory.pagination.next')}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {itemToDelete && (
        <div className="modal-overlay" onClick={() => setItemToDelete(null)}>
          <div
            className="modal-content inventory-delete-confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="inventory-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="inventory-delete-title">{t('common.delete')}</h2>
              <button
                type="button"
                className="close-btn"
                onClick={() => setItemToDelete(null)}
                aria-label={t('common.close')}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p>{t('common.confirmDelete')}</p>
              <p className="text-secondary">
                <strong>{itemToDelete.name}</strong>
              </p>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setItemToDelete(null)}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-danger inventory-delete-confirm-btn"
                onClick={handleDelete}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {suggestOpen && (
        <SuggestInventoryItemModal
          categories={categories}
          onClose={() => setSuggestOpen(false)}
          onCreated={() => {
            setSuggestOpen(false);
            showFeedback('success', t('inventory.suggestions.createdFeedback'));
          }}
        />
      )}

      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingItem ? t('inventory.editItem') : t('inventory.newItem')}</h2>
              <button
                type="button"
                className="close-btn"
                onClick={() => setModalOpen(false)}
                aria-label={t('common.close')}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit} noValidate>
              <div className="modal-body">
                <p className="required-legend">* {t('common.requiredFieldsLegend')}</p>
                <div className={`form-group${formErrors.name ? ' has-error' : ''}`}>
                  <label htmlFor="inventory-name">{t('inventory.name')} *</label>
                  <input
                    id="inventory-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setField('name', e.target.value)}
                    aria-required="true"
                    aria-invalid={!!formErrors.name}
                  />
                  {formErrors.name && (
                    <span className="field-error" role="alert">
                      {formErrors.name}
                    </span>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="inventory-kind">
                      {t('inventory.kind')} <HelpIcon topicKey="inventory.kind" />
                    </label>
                    <select
                      id="inventory-kind"
                      value={formData.kind}
                      onChange={(e) => setField('kind', e.target.value)}
                    >
                      {KINDS.map((kind) => (
                        <option key={kind} value={kind}>
                          {kindLabel(kind)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="inventory-unit">{t('inventory.unit')}</label>
                    <select
                      id="inventory-unit"
                      value={formData.unit}
                      onChange={(e) => setField('unit', e.target.value)}
                    >
                      {CANONICAL_UNITS.map((u) => (
                        <option key={u} value={u}>
                          {t(`inventory.units.${u}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="inventory-category">{t('inventory.category')}</label>
                    <input
                      id="inventory-category"
                      type="text"
                      list="inventory-categories"
                      value={formData.category}
                      onChange={(e) => setField('category', e.target.value)}
                      placeholder={t('inventory.categoryPlaceholder')}
                    />
                    <datalist id="inventory-categories">
                      {categories.map((category) => (
                        <option key={category} value={category} />
                      ))}
                    </datalist>
                  </div>
                  <div className="form-group">
                    <label htmlFor="inventory-supplier">{t('inventory.supplier')}</label>
                    <select
                      id="inventory-supplier"
                      value={formData.supplierId}
                      onChange={(e) => setField('supplierId', e.target.value)}
                    >
                      <option value="">{t('inventory.noSupplier')}</option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.supplierId} value={supplier.supplierId}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="inventory-refcost">
                      {t('inventory.refCost')} ({t(`inventory.units.${formData.unit}`)})
                    </label>
                    <input
                      id="inventory-refcost"
                      type="number"
                      value={formData.refCost}
                      onChange={(e) => setField('refCost', e.target.value)}
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="inventory-sku">{t('inventory.sku')}</label>
                    <input
                      id="inventory-sku"
                      type="text"
                      value={formData.sku}
                      onChange={(e) => setField('sku', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={formData.trackStock}
                      onChange={(e) => setField('trackStock', e.target.checked)}
                    />
                    {t('inventory.trackStock')} <HelpIcon topicKey="inventory.trackStock" />
                  </label>
                  <small className="help-text">{t('inventory.trackStockHelp')}</small>
                </div>

                {formData.trackStock && (
                  <>
                    <div className="form-row">
                      <div className={`form-group${formErrors.stock ? ' has-error' : ''}`}>
                        <label htmlFor="inventory-stock">
                          {t('inventory.stock')}
                          {!editingItem && ' *'}
                        </label>
                        <input
                          id="inventory-stock"
                          type="number"
                          value={formData.stock}
                          onChange={(e) => setField('stock', e.target.value)}
                          min="0"
                          step="0.01"
                          aria-required={!editingItem}
                          aria-invalid={!!formErrors.stock}
                        />
                        {formErrors.stock && (
                          <span className="field-error" role="alert">
                            {formErrors.stock}
                          </span>
                        )}
                      </div>
                      {editingItem && (
                        <div className="form-group">
                          <label htmlFor="inventory-minstock-local">
                            {t('inventory.minStockLocal')}
                          </label>
                          <input
                            id="inventory-minstock-local"
                            type="number"
                            value={formData.minStockOverride}
                            onChange={(e) => setField('minStockOverride', e.target.value)}
                            min="0"
                            step="0.01"
                            placeholder={t('inventory.minStockLocalPlaceholder', {
                              value: formData.defaultMinStock === '' ? 0 : formData.defaultMinStock,
                            })}
                          />
                          <small className="help-text">{t('inventory.minStockLocalHelp')}</small>
                        </div>
                      )}
                      <div className="form-group">
                        <label htmlFor="inventory-minstock">{t('inventory.defaultMinStock')}</label>
                        <input
                          id="inventory-minstock"
                          type="number"
                          value={formData.defaultMinStock}
                          onChange={(e) => setField('defaultMinStock', e.target.value)}
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="inventory-expiration">{t('inventory.expiration')}</label>
                      <input
                        id="inventory-expiration"
                        type="date"
                        value={formData.expirationDate}
                        onChange={(e) => setField('expirationDate', e.target.value)}
                      />
                      <small className="help-text">{t('inventory.expirationHelp')}</small>
                    </div>
                  </>
                )}

                {editingItem && (
                  <div className="form-group">
                    <label className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setField('isActive', e.target.checked)}
                      />
                      {t('inventory.isActive')}
                    </label>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setModalOpen(false)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default InventoryPage;
